import * as React from 'react';
import type {IEventTemplate} from '../../../types/IEventTemplate';
import type {SequenceItem} from '../../../types/IEventTemplateMetadata';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  CollapsibleCard,
  CollapsibleCardContent,
} from '@spaced-out/ui-design-system/lib/components/CollapsibleCard';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {SequenceDayItem} from './sequence-day-item';
import {useBookingMachine} from '../../../hooks/useBookingMachine';
import {selectFormattedSelectedDateForDay} from '../../../selectors/booking-selector';
import {useSelector} from '@xstate/react';
import {useRootMachine} from '../../../hooks/useRootMachine';
import {selectTimezone} from '../../../selectors/timezoneSelectors';
import css from './sequential-booking-info.css';
import {SubTitleSmall} from '@spaced-out/ui-design-system/lib/components/Text';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa/qa';


interface DayCardProps {
  dayNumber: number;
  sequenceDay: SequenceItem;
  eventTemplate: IEventTemplate;
  roundNumberOffset?: number;
  index: number;
  testId?: string;
}

export const DayCard = ({
  dayNumber,
  sequenceDay,
  eventTemplate,
  roundNumberOffset = 0,
  index,
  testId,
}: DayCardProps) => {
  const {t, language} = useLabelTranslation();
  const {bookingActor} = useBookingMachine();
  const {rootActor} = useRootMachine();
  const timezone = useSelector(rootActor, selectTimezone);

  const formattedDate = useSelector(bookingActor, (state) =>
    selectFormattedSelectedDateForDay(
      state,
      dayNumber,
      timezone || '',
      language,
    ),
  );

  const ItemTitleContent = () => (
    <div
      className={css.itemTitleContent}
      data-testid={generateTestId({base: testId, slot: 'item-title-content'})}
    >
      <SubTitleSmall
        testId={generateTestId({
          base: testId,
          slot: 'item-title-content-sub-title',
        })}
      >
        {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
        {`${t('Day', 'Day')} ${dayNumber}`}
      </SubTitleSmall>
      {formattedDate && (
        <Chip
          semantic="primary"
          size="medium"
          testId={generateTestId({
            base: testId,
            slot: 'item-title-content-chip',
          })}
        >
          {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
          {formattedDate}
        </Chip>
      )}
    </div>
  );

  return (
    <CollapsibleCard
      isOpen={true}
      classNames={{
        wrapper: css.collapsibleCardWrapper,
      }}
      key={`day-${index}`}
      paddingSize="small"
      title={<ItemTitleContent />}
      testId={generateTestId({base: testId, slot: 'collapsible-card'})}
    >
      <CollapsibleCardContent
        className={css.sequenceDayContent}
        data-testid={generateTestId({
          base: testId,
          slot: 'collapsible-card-content',
        })}
      >
        <SequenceDayItem
          sequenceDay={sequenceDay}
          eventTemplate={eventTemplate}
          roundNumberOffset={roundNumberOffset}
          testId={generateTestId({base: testId, slot: 'sequence-day-item'})}
        />
      </CollapsibleCardContent>
    </CollapsibleCard>
  );
};
